"use client";

import { useMemo, useCallback, Fragment } from "react";
import { compareFolders, type SessionListItem, type FolderResponse } from "@/app/lib/types";
import FolderItem from "./FolderItem";
import SessionItem from "./SessionItem";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface FoldersTabProps {
  folders: FolderResponse[];
  sessions: SessionListItem[];
  activeSessionId?: string;
  expandedFolders: Set<string>;
  onToggleFolder: (id: string) => void;
  onSessionClick: (id: string) => void;
  onSessionDelete: (id: string) => void;
  onSessionRename: (id: string, title: string) => void;
  onSessionFlag: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void;
  onMoveToNewFolder?: (sessionId: string) => void;
  onShare?: (id: string) => void;
  onNewChatInFolder: (folderId: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onSetFolderSeparator?: (id: string, value: boolean) => void;
  onDeleteFolder: (id: string) => void;
  onReorderFolders?: (orderedIds: string[]) => void;
  onCreateFolder: () => void;
  currentUserEmail?: string;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  newlyCreatedFolderId?: string | null;
  onAutoEditConsumed?: () => void;
}

export default function FoldersTab({
  folders,
  sessions,
  activeSessionId,
  expandedFolders,
  onToggleFolder,
  onSessionClick,
  onSessionDelete,
  onSessionRename,
  onSessionFlag,
  onMoveToFolder,
  onMoveSessionToFolder,
  onMoveToNewFolder,
  onShare,
  onNewChatInFolder,
  onRenameFolder,
  onSetFolderSeparator,
  onDeleteFolder,
  onReorderFolders,
  onCreateFolder,
  currentUserEmail,
  selectionMode,
  selectedIds,
  onToggleSelect,
  newlyCreatedFolderId,
  onAutoEditConsumed,
}: FoldersTabProps) {
  const sortedFolders = useMemo(
    () => [...folders].sort(compareFolders),
    [folders]
  );

  const sortedFolderIds = useMemo(
    () => sortedFolders.map((f) => f.id),
    [sortedFolders]
  );

  const sessionsByFolder = useMemo(() => {
    const map: Record<string, SessionListItem[]> = {};
    for (const folder of folders) {
      map[folder.id] = [];
    }
    for (const session of sessions) {
      if (session.folder_id && map[session.folder_id]) {
        map[session.folder_id].push(session);
      }
    }
    // Chats inside a folder sort exactly like Recent: flagged pinned to top,
    // then reverse-chronological by last MESSAGE (last_message_at, not the
    // metadata-bumped updated_at; folders themselves stay manually orderable).
    const lastActivity = (s: SessionListItem) =>
      new Date(s.last_message_at || s.updated_at).getTime();
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const flagDiff = (b.flagged ? 1 : 0) - (a.flagged ? 1 : 0);
        if (flagDiff !== 0) return flagDiff;
        const diff = lastActivity(b) - lastActivity(a);
        if (diff !== 0) return diff;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    }
    return map;
  }, [folders, sessions]);

  const unfiledSessions = useMemo(
    () =>
      sessions
        .filter((s) => !s.folder_id)
        .sort((a, b) => {
          // Same as Recent: flagged pinned, then last MESSAGE date (not updated_at).
          const flagDiff = (b.flagged ? 1 : 0) - (a.flagged ? 1 : 0);
          if (flagDiff !== 0) return flagDiff;
          const aT = new Date(a.last_message_at || a.updated_at).getTime();
          const bT = new Date(b.last_message_at || b.updated_at).getTime();
          if (bT !== aT) return bT - aT;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }),
    [sessions]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorderFolders) return;
      const oldIndex = sortedFolderIds.indexOf(active.id as string);
      const newIndex = sortedFolderIds.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return;
      const newOrder = [...sortedFolderIds];
      newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, active.id as string);
      onReorderFolders(newOrder);
    },
    [sortedFolderIds, onReorderFolders]
  );

  return (
    <div>
      {/* New Folder button */}
      <button
        className="btn-ghost"
        onClick={onCreateFolder}
        style={{
          width: "100%",
          justifyContent: "flex-start",
          padding: "8px 12px",
          gap: "8px",
          color: "var(--accent)",
          fontSize: "13px",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M8 3v10M3 8h10" />
        </svg>
        New Folder
      </button>

      {/* Sortable Folders */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedFolderIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedFolders.map((folder) => (
            <Fragment key={folder.id}>
              {folder.separator_before && (
                // Span from the chevron (16px in) to just past the new-chat icon (~30px from right).
                <div style={{ height: "2px", background: "#484848", borderRadius: "1px", margin: "2px 30px 2px 16px" }} />
              )}
            <FolderItem
              folder={folder}
              sessions={sessionsByFolder[folder.id] || []}
              isExpanded={expandedFolders.has(folder.id)}
              onToggle={() => onToggleFolder(folder.id)}
              activeSessionId={activeSessionId}
              onSessionClick={onSessionClick}
              onSessionDelete={onSessionDelete}
              onSessionRename={onSessionRename}
              onSessionFlag={onSessionFlag}
              onMoveToFolder={onMoveToFolder}
              onMoveSessionToFolder={onMoveSessionToFolder}
              onMoveToNewFolder={onMoveToNewFolder}
              folders={folders}
              onShare={onShare}
              onNewChatInFolder={onNewChatInFolder}
              onRenameFolder={onRenameFolder}
              onSetFolderSeparator={onSetFolderSeparator}
              onDeleteFolder={onDeleteFolder}
              currentUserEmail={currentUserEmail}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              autoEdit={folder.id === newlyCreatedFolderId}
              onAutoEditConsumed={onAutoEditConsumed}
            />
            </Fragment>
          ))}
        </SortableContext>
      </DndContext>

      {/* Unfiled sessions */}
      {unfiledSessions.length > 0 && (
        <div style={{ marginTop: "8px" }}>
          <div
            style={{
              padding: "6px 12px",
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Unfiled
          </div>
          {unfiledSessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isActive={activeSessionId === session.id}
              onClick={() => onSessionClick(session.id)}
              onDelete={onSessionDelete}
              onRename={onSessionRename}
              onFlag={onSessionFlag}
              onMoveToFolder={onMoveToFolder}
              onMoveSessionToFolder={onMoveSessionToFolder}
              onMoveToNewFolder={onMoveToNewFolder}
              folders={folders}
              onShare={onShare}
              currentUserEmail={currentUserEmail}
              selectionMode={selectionMode}
              selected={selectedIds?.has(session.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
